import React, { useState, useCallback, useEffect, useRef } from 'react';
import { POI, ImageData, Map } from '../../../../model/FestivalData';
import { FiPlus, FiUpload } from 'react-icons/fi';
import ImageEditor from '../ImageEditor/ImageEditor';
import { generateCanonicalName, generateIdentifier } from '../../../../utils/identifiers';
import { getImageUrl } from '../../../../services/imageService';
import { DataImportService } from '../../../../services/dataImportService';
import Modal from '../../../../components/Modal';
import { useDialogs } from '../../../../components/Dialogs';
import PageHeader from '../../../../components/PageHeader';
import OverflowMenu from '../../../../components/OverflowMenu';
import SearchField from '../../../../components/SearchField';
import { useMessages } from '../../../../i18n/I18n';
import './PoisEditor.css';

interface PoisEditorProps {
  pois: POI[];
  onChange: (newPois: POI[]) => void;
  getApiImageUrl: (identifier: string) => string;
  maps: Map[];
}

const PoisEditor: React.FC<PoisEditorProps> = ({ pois, onChange, getApiImageUrl, maps }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [editingPoi, setEditingPoi] = useState<POI | null>(null);
  const [unsavedChanges, setUnsavedChanges] = useState<Partial<POI> | null>(null);
  const [tempImages, setTempImages] = useState<Record<string, { icon?: string; main_image?: string }>>({});
  const [selectedMapId, setSelectedMapId] = useState<string | null>(maps[0]?.identifier || null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showClearAllModal, setShowClearAllModal] = useState(false);
  const { confirm, alert } = useDialogs();
  const t = useMessages('places');
  const common = useMessages('common');

  const filteredPois = pois.filter(poi => {
    const matchesSearch = poi.name.toLowerCase().includes(searchTerm.toLowerCase());
    if (selectedMapId === null) {
      return matchesSearch && (!poi.map_identifier || poi.map_identifier === '' || !maps.find(m => m.identifier === poi.map_identifier));
    }
    return matchesSearch && poi.map_identifier === selectedMapId;
  });

  const unsavedPoiId = "unsaved_poi_id";

  const handleAddPoi = () => {
    // Calculate next order for scenes
    const scenePois = pois.filter(p => p.category === 'scene');
    const maxOrder = scenePois.reduce((max, p) => Math.max(max, p.order ?? 0), 0);
    
    const newPoi: POI = {
      category: 'scene',
      identifier: unsavedPoiId,
      name: t.newPlace,
      map_identifier: selectedMapId || '',
      relative_coordinates: { x: 0.5, y: 0.5 },
      icon: null,
      main_image: null,
      description: '',
      order: maxOrder + 1
    };
    setEditingPoi(newPoi);
    setUnsavedChanges(null);
    setTempImages({});
  };

  const handleEditPoi = (poi: POI) => {
    setEditingPoi({...poi});
    setUnsavedChanges(null);
    setTempImages({});
  };

  const handleSavePoi = useCallback((poiToSave: POI) => {
    const canonicalName = generateCanonicalName(poiToSave.name);
    
    const newIdentifier = poiToSave.identifier === unsavedPoiId
      ? generateIdentifier(canonicalName)
      : poiToSave.identifier;
    
    const updatedPoi: POI = { 
      ...poiToSave,
      ...unsavedChanges,
      identifier: newIdentifier
    };
    
    const updatedPois = pois.map(poi => 
      poi.identifier === poiToSave.identifier ? updatedPoi : poi
    );

    if (!updatedPois.some(poi => poi.identifier === poiToSave.identifier)) {
      updatedPois.push(updatedPoi);
    }

    if (tempImages[poiToSave.identifier]) {
      setTempImages(prev => ({
        ...prev,
        [newIdentifier]: prev[poiToSave.identifier]
      }));
    }

    onChange(updatedPois);
    setEditingPoi(null);
    setUnsavedChanges(null);
  }, [pois, onChange, unsavedChanges, tempImages]);

  const handleRemovePoi = async (identifier: string) => {
    const poiToRemove = pois.find(poi => poi.identifier === identifier);
    if (poiToRemove && await confirm(t.deleteConfirm(poiToRemove.name), {
      title: t.deleteTitle,
      confirmLabel: common.delete,
      danger: true,
    })) {
      const updatedPois = pois.filter(poi => poi.identifier !== identifier);
      onChange(updatedPois);
      setEditingPoi(null);
      setUnsavedChanges(null);
      setTempImages(prev => {
        const { [identifier]: removed, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleImageChange = (imageType: 'icon' | 'main_image', newImage: ImageData | undefined) => {
    if (editingPoi) {
      setUnsavedChanges(prev => ({
        ...prev,
        [imageType]: newImage || null
      }));

      if (newImage && newImage.type === 'ApiImage' && 'file' in newImage && newImage.file) {
        setTempImages(prev => ({
          ...prev,
          [editingPoi.identifier]: {
            ...prev[editingPoi.identifier],
            [imageType]: URL.createObjectURL(newImage.file as Blob)
          }
        }));
      } else {
        setTempImages(prev => {
          const updated = { ...prev };
          if (updated[editingPoi.identifier]) {
            delete updated[editingPoi.identifier][imageType as keyof typeof updated[typeof editingPoi.identifier]];
            if (Object.keys(updated[editingPoi.identifier]).length === 0) {
              delete updated[editingPoi.identifier];
            }
          }
          return updated;
        });
      }
    }
  };

  const getPoiImageSrc = (poi: POI, imageType: 'icon' | 'main_image'): string | undefined => {
    if (tempImages[poi.identifier]?.[imageType]) {
      return tempImages[poi.identifier][imageType];
    }
    return getImageUrl(poi[imageType], getApiImageUrl);
  };

  const getPoiPosition = (poi: POI) => {
    const { x, y } = poi.relative_coordinates;
    
    if (x < 0 || x > 1 || y < 0 || y > 1) return null;

    return { x: x * 100, y: y * 100 };
  };

  const mapContainerRef = useRef<HTMLDivElement>(null);
  const draggingPoiRef = useRef<string | null>(null);

  const handlePoiDragStart = (e: React.MouseEvent, poiId: string) => {
    e.stopPropagation();
    draggingPoiRef.current = poiId;
  };

  const handlePoiDragEnd = () => {
    draggingPoiRef.current = null;
  };

  // Releasing the button anywhere drops the pin: outside the plan, it stays where it left the plan
  useEffect(() => {
    const dropPin = () => {
      draggingPoiRef.current = null;
    };
    window.addEventListener('mouseup', dropPin);
    return () => window.removeEventListener('mouseup', dropPin);
  }, []);

  // Add these refs to track map image dimensions
  const mapImageRef = useRef<HTMLImageElement>(null);
  const mapImageBoundsRef = useRef<{ width: number; height: number; left: number; top: number }>({ width: 0, height: 0, left: 0, top: 0 });

  // Add this effect to update map bounds when image loads or container resizes
  useEffect(() => {
    const updateMapBounds = () => {
      const img = mapImageRef.current;
      if (img) {
        const rect = img.getBoundingClientRect();
        mapImageBoundsRef.current = {
          width: rect.width,
          height: rect.height,
          left: rect.left,
          top: rect.top
        };
      }
    };

    updateMapBounds();
    window.addEventListener('resize', updateMapBounds);
    return () => window.removeEventListener('resize', updateMapBounds);
  }, []);

  // Update handleMapClick to calculate coordinates relative to the wrapper
  const handleMapClick = (e: React.MouseEvent) => {
    const wrapper = mapImageRef.current?.parentElement;
    if (!wrapper) return;

    const rect = wrapper.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));

    if (draggingPoiRef.current) {
      const updatedPois = pois.map(poi => {
        if (poi.identifier === draggingPoiRef.current) {
          return {
            ...poi,
            relative_coordinates: { x, y }
          };
        }
        return poi;
      });
      onChange(updatedPois);
    }
  };

  const handleMapImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    const aspectRatio = (img.naturalHeight / img.naturalWidth) * 100;
    img.parentElement!.style.paddingTop = `${aspectRatio}%`;
    
    const rect = img.getBoundingClientRect();
    mapImageBoundsRef.current = {
      width: rect.width,
      height: rect.height,
      left: rect.left,
      top: rect.top
    };
  };

  const renderPoiMarker = (poi: POI) => {
    const position = getPoiPosition(poi);
    if (!position) return null;

    return (
      <div 
        key={poi.identifier}
        className={`poi-marker ${poi.category} ${draggingPoiRef.current === poi.identifier ? 'dragging' : ''}`}
        style={{
          left: `${position.x}%`,
          top: `${position.y}%`,
          zIndex: draggingPoiRef.current === poi.identifier ? 1000 : 1
        }}
        onMouseDown={(e) => handlePoiDragStart(e, poi.identifier)}
        onMouseUp={handlePoiDragEnd}
        onClick={(e) => {
          e.stopPropagation();
          if (!draggingPoiRef.current) {
            handleEditPoi(poi);
          }
        }}
        title={poi.name}
      />
    );
  };

  const unattributedCount = pois.filter(poi => !maps.some(map => map.identifier === poi.map_identifier)).length;

  const renderMapTabs = () => (
    <div className="segmented map-tabs">
      {maps.map(map => (
        <button
          type="button"
          key={map.identifier}
          aria-pressed={selectedMapId === map.identifier}
          onClick={() => setSelectedMapId(map.identifier)}
        >
          {map.name || t.untitledMap}
        </button>
      ))}
      <button
        type="button"
        aria-pressed={selectedMapId === null}
        onClick={() => setSelectedMapId(null)}
      >
        {t.notOnMapCount(unattributedCount)}
      </button>
    </div>
  );

  const renderMapSection = () => {
    if (!maps || !maps.length) {
      return (
        <div className="map-placeholder">
          <p>{t.noMap}</p>
          <button 
            type="button"
            onClick={() => window.dispatchEvent(new CustomEvent('switchTab', { detail: 'map' }))}
            className="btn btn-primary"
          >
            {t.goToMaps}
          </button>
        </div>
      );
    }

    const selectedMap = selectedMapId ? maps.find(m => m.identifier === selectedMapId) : null;

    return (
      <>
        <div className="pois-map-card-header">{renderMapTabs()}</div>
        {selectedMap ? (
          <div 
            ref={mapContainerRef}
            className="map-container" 
            style={{ 
              position: 'relative', 
              marginBottom: '20px',
              width: '80%',
              margin: '0 auto',
              backgroundColor: '#f5f5f5',
              overflow: 'hidden',
            }}
            onClick={handleMapClick}
            onMouseMove={(e) => draggingPoiRef.current && handleMapClick(e)}
          >
            <div style={{
              position: 'relative',
              width: '100%',
              paddingTop: '75%',
            }}>
              <img 
                ref={mapImageRef}
                src={getImageUrl(selectedMap.image, getApiImageUrl)}
                alt={t.mapAlt} 
                style={{ 
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain'
                }}
                onLoad={handleMapImageLoad}
                draggable={false}
              />
              {filteredPois.map(poi => renderPoiMarker(poi))}
            </div>
          </div>
        ) : (
          <div className="unattributed-section">
            <p>{t.unattributed}</p>
          </div>
        )}
        {selectedMap && (
          <div className="pois-legend">
            <span className="pois-legend-item scene">{t.stage}</span>
            <span className="pois-legend-item food">{t.food}</span>
          </div>
        )}
      </>
    );
  };

  // Add this effect to clean up URLs
  useEffect(() => {
    return () => {
      // Clean up any object URLs when component unmounts
      Object.values(tempImages).forEach(images => {
        if (images.icon) URL.revokeObjectURL(images.icon);
        if (images.main_image) URL.revokeObjectURL(images.main_image);
      });
    };
  }, [tempImages]);

  const closePlace = () => {
    setEditingPoi(null);
    setUnsavedChanges(null);
    setTempImages({});
  };

  const renderPoiEditModal = () => (
    <Modal isOpen={editingPoi !== null} onClose={closePlace} variant="sheet">
      {editingPoi && (
        <>
          <h3>{editingPoi.identifier === unsavedPoiId ? t.addTitle : t.editTitle}</h3>

          <div className="poi-field">
            <label htmlFor="poi-name">{t.name}</label>
            <input
              id="poi-name"
              type="text"
              value={editingPoi.name}
              onChange={(e) => setEditingPoi({ ...editingPoi, name: e.target.value })}
              placeholder={t.namePlaceholder}
            />
          </div>
          <div className="poi-field-row">
            <div className="poi-field">
              <label htmlFor="poi-category">{t.type}</label>
              <select
                id="poi-category"
                value={editingPoi.category}
                onChange={(e) => setEditingPoi({ ...editingPoi, category: e.target.value as 'scene' | 'food' })}
              >
                <option value="scene">{t.stage}</option>
                <option value="food">{t.food}</option>
              </select>
            </div>
            {editingPoi.category === 'scene' && (
              <div className="poi-field">
                <label htmlFor="poi-order">{t.timetableOrder}</label>
                <input
                  id="poi-order"
                  type="number"
                  value={editingPoi.order ?? 0}
                  onChange={(e) => setEditingPoi({ 
                    ...editingPoi, 
                    order: parseInt(e.target.value) || 0 
                  })}
                  placeholder={t.orderPlaceholder}
                  min="0"
                />
              </div>
            )}
          </div>
          <div className="poi-field">
            <label htmlFor="poi-map">{t.map}</label>
            <select
              id="poi-map"
              value={editingPoi.map_identifier}
              onChange={(e) => setEditingPoi({ 
                ...editingPoi, 
                map_identifier: e.target.value 
              })}
            >
              <option key="" value="">
                  {t.notOnMap}
                </option>
              {maps.map(map => (
                <option key={map.identifier} value={map.identifier}>
                  {map.name || t.untitledMap}
                </option>
              ))}
            </select>
          </div>
          <div className="poi-field-row">
            <div className="poi-field">
              <label htmlFor="poi-x">{t.positionX}</label>
            <input
              id="poi-x"
              type="number"
              value={editingPoi.relative_coordinates.x}
              onChange={(e) => setEditingPoi({ 
                ...editingPoi, 
                relative_coordinates: { 
                  ...editingPoi.relative_coordinates, 
                  x: Math.max(0, Math.min(1, parseFloat(e.target.value))) 
                }
              })}
              placeholder={t.positionXPlaceholder}
              step="0.01"
              min="0"
              max="1"
            />
            </div>
            <div className="poi-field">
              <label htmlFor="poi-y">{t.positionY}</label>
            <input
              id="poi-y"
              type="number"
              value={editingPoi.relative_coordinates.y}
              onChange={(e) => setEditingPoi({ 
                ...editingPoi, 
                relative_coordinates: { 
                  ...editingPoi.relative_coordinates, 
                  y: Math.max(0, Math.min(1, parseFloat(e.target.value))) 
                }
              })}
              placeholder={t.positionYPlaceholder}
              step="0.01"
              min="0"
              max="1"
            />
            </div>
          </div>
          <p className="poi-field-help">{t.positionHelp}</p>
          <div className="poi-field">
            <label htmlFor="poi-description">{t.description}</label>
            <textarea
              id="poi-description"
              value={editingPoi.description}
              onChange={(e) => setEditingPoi({ ...editingPoi, description: e.target.value })}
              placeholder={t.descriptionPlaceholder}
            />
          </div>
          <h4>{t.icon}</h4>
          <ImageEditor
            currentImage={(unsavedChanges?.icon as ImageData) || editingPoi.icon || undefined}
            onChange={(newImage) => handleImageChange('icon', newImage)}
            getApiImageUrl={getApiImageUrl}
          />
          <h4>{t.mainImage}</h4>
          <ImageEditor
            currentImage={(unsavedChanges?.main_image as ImageData) || editingPoi.main_image || undefined}
            onChange={(newImage) => handleImageChange('main_image', newImage)}
            getApiImageUrl={getApiImageUrl}
          />
          <div className="modal-actions">
            <button type="button" onClick={() => handleSavePoi(editingPoi)}>{common.save}</button>
            <button type="button" onClick={closePlace}>{common.cancel}</button>
            {editingPoi.identifier !== unsavedPoiId && (
              <button type="button" className="danger" onClick={() => handleRemovePoi(editingPoi.identifier)}>{t.remove}</button>
            )}
          </div>
        </>
      )}
    </Modal>
  );

  const handleFileImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const data = DataImportService.parseJsonFile(content);
        
        if (!data.pois || !Array.isArray(data.pois)) {
          alert(t.invalidFormat, { title: t.importFailed });
          return;
        }

        const validPois: POI[] = [];
        const errors: string[] = [];
        let refused = 0;

        data.pois.forEach((poi: any) => {
          const result = DataImportService.validatePOI(poi, maps);
          if (result.isValid) {
            validPois.push(...result.data);
          } else {
            refused += 1;
            errors.push(...result.errors.map(error => `[${poi.name || t.unknown}] ${error}`));
          }
        });

        if (errors.length > 0) {
          console.warn('Validation errors:', errors);
        }

        if (validPois.length === 0) {
          alert(t.noValidPois, { title: t.importFailed });
          return;
        }

        onChange(validPois);
        const message = t.imported(validPois.length, refused, errors);
        alert(message, { title: t.importFinished });
      } catch (error) {
        console.error('Error parsing JSON:', error);
        alert(t.parseError, { title: t.importFailed });
      }
    };
    reader.readAsText(file);
  };

  const handleClearAllPois = () => {
    onChange([]);
    setShowClearAllModal(false);
  };

  const stages = filteredPois
    .filter(poi => poi.category === 'scene')
    .sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
  const otherPlaces = filteredPois
    .filter(poi => poi.category !== 'scene')
    .sort((a, b) => a.name.localeCompare(b.name));

  const renderPlaceRow = (poi: POI) => (
    <button type="button" key={poi.identifier} className="poi-row" onClick={() => handleEditPoi(poi)}>
      {getPoiImageSrc(poi, 'icon')
        ? <img src={getPoiImageSrc(poi, 'icon')} alt="" className="poi-row-icon" />
        : <span className={`poi-row-dot ${poi.category}`} aria-hidden="true" />}
      <span className="poi-row-name">{poi.name}</span>
      {poi.category === 'scene' && poi.order !== undefined && (
        <span className="poi-row-order" aria-label={t.orderOf(poi.order)}>{poi.order}</span>
      )}
    </button>
  );

  return (
    <section className="pois-editor">
      <PageHeader
        title={t.title}
        count={pois.length}
        description={t.subtitle}
      >
        <label className="btn import-button">
          <input
            type="file"
            accept=".json"
            onChange={handleFileImport}
            ref={fileInputRef}
          />
          <FiUpload aria-hidden="true" />
          {t.importJson}
        </label>
        <button type="button" className="btn btn-primary" onClick={handleAddPoi}>
          <FiPlus aria-hidden="true" />
          {t.addPlace}
        </button>
        <OverflowMenu items={[{
          label: t.clearAllMenu,
          danger: true,
          disabled: pois.length === 0,
          onSelect: () => setShowClearAllModal(true),
        }]} />
      </PageHeader>
      <div className="pois-layout">
        <div className="pois-map-card">
          {renderMapSection()}
        </div>
        <aside className="pois-list">
          <div className="pois-list-search">
            <SearchField value={searchTerm} onChange={setSearchTerm} placeholder={t.search} />
          </div>
          {stages.length > 0 && (
            <>
              <div className="pois-list-heading"><span>{t.stages}</span><span>{t.timetableOrder}</span></div>
              {stages.map(renderPlaceRow)}
            </>
          )}
          {otherPlaces.length > 0 && (
            <>
              <div className="pois-list-heading"><span>{t.food}</span></div>
              {otherPlaces.map(renderPlaceRow)}
            </>
          )}
          {filteredPois.length === 0 && (
            <p className="pois-list-empty">
              {searchTerm ? t.noMatch : t.empty}
            </p>
          )}
        </aside>
      </div>
      {renderPoiEditModal()}
      <Modal
        isOpen={showClearAllModal}
        onClose={() => setShowClearAllModal(false)}
      >
        <div className="modal-header">
          <h3>{t.clearAllTitle}</h3>
          <button className="modal-close" onClick={() => setShowClearAllModal(false)} aria-label={common.close}>×</button>
        </div>
        <p>{t.clearAllConfirm(pois.length)}</p>
        <div className="modal-actions">
          <button 
            className="modal-delete"
            onClick={handleClearAllPois}
          >
            {t.clearAll}
          </button>
          <button 
            className="modal-cancel"
            onClick={() => setShowClearAllModal(false)}
          >
            {common.cancel}
          </button>
        </div>
      </Modal>
    </section>
  );
};

export default PoisEditor;
